import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { 
  Search, ShoppingCart, Heart, User, Menu, X, ChevronRight, 
  Sun, Moon, Crown, Wallet, Home, Package, Sparkles // ✅ Sparkles added here
} from 'lucide-react';
import { useCart } from '../context/CartContext';
import { useTheme } from '../context/ThemeContext';
import { useAuth } from '../context/AuthContext';

const Navbar = () => {
    const [isMenuOpen, setIsMenuOpen] = useState(false);
    const [searchTerm, setSearchTerm] = useState("");
    const [scrollProgress, setScrollProgress] = useState(0);
    const navigate = useNavigate();
    const location = useLocation();
    
    const { cartCount } = useCart(); 
    const { isDarkMode, toggleTheme } = useTheme();
    const { user } = useAuth(); 

    useEffect(() => {
        const handleScroll = () => {
            const position = window.scrollY;
            setScrollProgress(position > 50 ? 0.1 : 0);
        };
        window.addEventListener("scroll", handleScroll);
        return () => window.removeEventListener("scroll", handleScroll);
    }, []);

    const handleSearch = (e) => {
        if (e.key === 'Enter' && searchTerm.trim() !== "") {
            navigate(`/search?query=${searchTerm}`);
            setIsMenuOpen(false);
            setSearchTerm("");
        }
    };

    const categories = [
        "Hoodies", "Jeans", "T-Shirts", "Shirts", 
        "Sweatshirts", "Baggy Pants", "Track Pants", "Jackets",
        "Shoes", "Watches", "Caps", "Belts"
    ];

    const isActive = (path) => location.pathname === path;

    return (
        <>
            {/* --- TOP NAVBAR --- */}
            <nav className={`px-[20px] py-3 md:py-4 sticky top-0 z-[100] shadow-2xl border-b transition-all duration-500 ${
                isDarkMode 
                ? 'bg-[#050505]/90 backdrop-blur-xl text-white border-white/5' 
                : 'bg-white/90 backdrop-blur-xl text-black border-black/5'
            }`}>
                <div className="max-w-[1400px] mx-auto flex items-center justify-between gap-4">
                    
                    <div className="flex items-center gap-2 md:gap-6">
                        <button 
                            onClick={() => setIsMenuOpen(true)}
                            className={`p-2 rounded-xl transition-all ${isDarkMode ? 'hover:bg-white/10 text-zinc-400' : 'hover:bg-black/5 text-zinc-600'}`}
                        >
                            <Menu size={26} />
                        </button>

                        <button 
                            onClick={() => navigate('/')}
                            className="flex items-center gap-3 group outline-none active:scale-95 transition-transform"
                        >
                            <div className={`w-9 h-9 md:w-11 md:h-11 rounded-xl md:rounded-2xl flex items-center justify-center transition-all duration-500 shadow-lg 
                                ${scrollProgress > 0 
                                    ? (isDarkMode ? 'bg-emerald-500 shadow-emerald-500/20' : 'bg-black shadow-black/20 ') 
                                    : (isDarkMode ? 'bg-emerald-500 shadow-emerald-500/10' : 'bg-black shadow-black/10')
                                } group-hover:rotate-12 group-hover:scale-105`}
                            >
                                <Crown size={20} className={isDarkMode ? 'text-black' : 'text-white'} strokeWidth={3} />
                            </div>
                            <span className="font-[1000] italic text-xl md:text-2xl tracking-tighter uppercase">
                                WE<span className="text-emerald-500">.</span>THRIFT
                            </span>
                        </button>
                    </div>

                    {/* Desktop Search */}
                    <div className="flex-1 max-w-lg relative hidden lg:block">
                        <input 
                            type="text" 
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            onKeyDown={handleSearch}
                            placeholder="SEARCH_VAULT..." 
                            className={`w-full py-3 px-6 pr-12 rounded-2xl text-[10px] font-black uppercase tracking-widest outline-none border transition-all ${
                                isDarkMode ? 'bg-white/5 border-white/10 text-white' : 'bg-black/5 border-black/10 text-black'
                            }`} 
                        />
                        <Search className="absolute right-4 top-3 cursor-pointer" size={16} />
                    </div>

                    {/* Icons Section */}
                    <div className="flex items-center gap-2 md:gap-5">

                        {/* ✨ AI STYLIST BUTTON (Desktop) */}
                        <Link to="/ai-stylist" className="relative group hidden md:block">
                            <div className="flex items-center gap-2 px-4 py-2 bg-emerald-500/10 border border-emerald-500/20 rounded-full hover:bg-emerald-500 hover:text-black transition-all duration-500">
                                <Sparkles size={16} className="text-emerald-500 group-hover:text-black animate-pulse" />
                                <span className="text-[10px] font-[1000] uppercase tracking-widest">AI Stylist</span>
                                
                                {/* Notification Glow */}
                                <span className="absolute -top-1 -right-1 flex h-2 w-2">
                                    <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75"></span>
                                    <span className="relative inline-flex rounded-full h-2 w-2 bg-emerald-500"></span>
                                </span>
                            </div>
                        </Link>
                        
                        {/* 💰 WALLET BALANCE */}
                        <div 
                            onClick={() => navigate('/refer')}
                            className={`flex items-center gap-2 px-3 py-2 rounded-xl border cursor-pointer transition-all active:scale-95 ${
                                isDarkMode ? 'bg-emerald-500/10 border-emerald-500/20 text-emerald-500' : 'bg-emerald-50 border-emerald-100 text-emerald-600'
                            }`}
                        >
                            <Wallet size={18} strokeWidth={2.5} />
                            <span className="font-black text-xs md:text-sm">₹{user?.walletBalance || 0}</span>
                        </div>

                        <button onClick={toggleTheme} className="p-2.5">
                            {isDarkMode ? <Sun size={20} className="text-yellow-400" /> : <Moon size={20} className="text-purple-600" />}
                        </button>

                        <Link to="/wishlist" className="hidden sm:flex p-2.5">
                            <Heart size={22} className={isDarkMode ? 'text-zinc-400' : 'text-zinc-600'} />
                        </Link>

                        <Link to="/profile" className="hidden sm:flex p-2.5">
                            <User size={22} className={isDarkMode ? 'text-zinc-400' : 'text-zinc-600'} />
                        </Link>

                        <Link to="/cart" className={`hidden sm:flex items-center gap-2 px-4 py-2.5 rounded-2xl ${
                            isDarkMode ? 'bg-white text-black' : 'bg-black text-white'
                        }`}>
                            <ShoppingCart size={20} />
                            {cartCount > 0 && <span className="font-black text-[11px]">{cartCount}</span>}
                        </Link>
                    </div>
                </div>
            </nav>

            {/* --- MOBILE BOTTOM NAVIGATION --- */}
            <div className={`fixed bottom-0 left-0 w-full z-[100] sm:hidden px-4 pb-4`}>
                <div className={`flex items-center justify-around py-3 px-2 rounded-[2.5rem] shadow-2xl backdrop-blur-2xl border transition-all ${
                    isDarkMode ? 'bg-black/80 border-white/10' : 'bg-white/80 border-black/5'
                }`}>
                    <Link to="/" className={`flex flex-col items-center p-2 transition-all ${isActive('/') ? 'text-emerald-500 scale-110' : 'text-zinc-500'}`}>
                        <Home size={22} strokeWidth={isActive('/') ? 3 : 2} />
                        <span className="text-[8px] font-black uppercase mt-1">Home</span>
                    </Link>

                    <Link to="/products" className={`flex flex-col items-center p-2 transition-all ${isActive('/products') ? 'text-emerald-500 scale-110' : 'text-zinc-500'}`}>
                        <Package size={22} strokeWidth={isActive('/products') ? 3 : 2} />
                        <span className="text-[8px] font-black uppercase mt-1">Explore</span>
                    </Link>

                    {/* ✨ MOBILE AI BUTTON (Centralized) */}
                    <Link to="/ai-stylist" className={`flex flex-col items-center p-2 transition-all ${isActive('/ai-stylist') ? 'text-emerald-500 scale-110' : 'text-zinc-500'}`}>
                        <div className={`p-2 rounded-full ${isActive('/ai-stylist') ? 'bg-emerald-500 text-black' : 'bg-emerald-500/10 text-emerald-500'}`}>
                            <Sparkles size={20} />
                        </div>
                        <span className="text-[8px] font-black uppercase mt-1">AI Stylist</span>
                    </Link>

                    <Link to="/cart" className={`relative flex flex-col items-center p-2 transition-all ${isActive('/cart') ? 'text-emerald-500 scale-110' : 'text-zinc-500'}`}>
                        <ShoppingCart size={22} strokeWidth={isActive('/cart') ? 3 : 2} />
                        {cartCount > 0 && (
                            <span className="absolute top-1 right-1 bg-red-500 text-white text-[8px] font-black px-1.5 py-0.5 rounded-full ring-2 ring-white">
                                {cartCount}
                            </span>
                        )}
                        <span className="text-[8px] font-black uppercase mt-1">Bag</span>
                    </Link>

                    <Link to="/profile" className={`flex flex-col items-center p-2 transition-all ${isActive('/profile') ? 'text-emerald-500 scale-110' : 'text-zinc-500'}`}>
                        <User size={22} strokeWidth={isActive('/profile') ? 3 : 2} />
                        <span className="text-[8px] font-black uppercase mt-1">Profile</span>
                    </Link>
                </div>
            </div>

            {/* --- SIDEBAR MENU --- */}
            <div 
                className={`fixed inset-0 bg-black/80 backdrop-blur-md z-[110] transition-opacity duration-500 ${isMenuOpen ? 'opacity-100 visible' : 'opacity-0 invisible'}`}
                onClick={() => setIsMenuOpen(false)}
            ></div>

            <div className={`fixed top-0 left-0 h-full w-[300px] z-[120] shadow-2xl transform transition-all duration-500 ${isMenuOpen ? 'translate-x-0' : '-translate-x-full'} ${isDarkMode ? 'bg-[#0a0a0a]' : 'bg-white'}`}>
                <div className={`flex items-center justify-between p-6 border-b ${isDarkMode ? 'border-white/5' : 'border-black/5'}`}>
                    <span className={`font-black italic text-xl ${isDarkMode ? 'text-white' : 'text-black'}`}>DIRECTORY</span>
                    <button onClick={() => setIsMenuOpen(false)} className="p-2"><X size={20} /></button>
                </div>

                <div className="p-6 overflow-y-auto h-[calc(100vh-80px)] no-scrollbar">
                    {categories.map((cat) => (
                        <Link 
                            key={cat}
                            to={`/category/${cat.toLowerCase().replace(" ", "-")}`}
                            onClick={() => setIsMenuOpen(false)}
                            className={`flex items-center justify-between w-full p-4 rounded-xl mb-1 transition-all ${
                                isDarkMode ? 'text-zinc-400 hover:bg-white/5' : 'text-zinc-600 hover:bg-black/5'
                            }`}
                        >
                            <span className="font-black text-sm uppercase italic">{cat}</span>
                            <ChevronRight size={18} />
                        </Link>
                    ))}
                </div>
            </div>
        </>
    );
};

export default Navbar;